import { NumberField } from "@/components/ui/number-field";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CalendarIcon,
  Clock,
  MapPin,
  Target,
  CheckSquare,
  Repeat,
  ExternalLink,
  Trash2,
  Edit,
  Eye,
  X,
  ChevronDown,
  ChevronUp,
  CalendarClock,
  Paperclip,
  Download,
  Share2,
  Copy,
} from "lucide-react";
import { InlineMediaPreview, isVideoMediaFile, isImageMediaFile } from "@/components/attachments/inline-media";
import DocumentUploadButton from "@/components/document-upload-button";
import ShareDialog from "@/components/share-dialog";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { apiRequest } from "@/lib/queryClient";
import { isFutureGoal } from "@/lib/goal-visibility";
import { preventDismissFromSiblingDialog } from "@/lib/dialog-guards";
import type { CalendarEvent, Goal } from "@shared/schema";
import { z } from "zod";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { INTERNAL_CALENDARS_QUERY_KEY, type InternalCalendar } from "@/components/internal-calendars-panel";

// Hand-written to avoid drizzle-zod + Zod 3.25 incompatibility (frontendCalendarEventSchema.extend() throws at module init)
const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  startDate: z.string(),
  startTime: z.string(),
  endDate: z.string(),
  endTime: z.string(),
  allDay: z.boolean().optional(),
  location: z.string().optional(),
  eventType: z.string().optional(),
  category: z.string().optional(),
  goalId: z.string().optional(),
  taskId: z.string().optional(),
  habitId: z.string().optional(),
  color: z.string().optional(),
  isRecurring: z.boolean().optional(),
  recurrenceRule: z.string().optional(),
  recurringEventId: z.string().optional(),
  externalCalendarId: z.string().optional(),
  externalEventId: z.string().optional(),
  isCompleted: z.boolean().optional(),
  repeatIfIncomplete: z.boolean().optional(),
  reminders: z.any().optional(),
  recurrenceFrequency: z.enum(["daily", "weekly", "bi-weekly", "monthly", "quarterly", "yearly"]).optional(),
  recurrenceCount: z.number().optional(),
  recurrenceEndDate: z.string().optional(),
  internalCalendarId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface EventDetailsDialogProps {
  event: CalendarEvent | null;
  onClose: () => void;
  goals?: Goal[];
  defaultMode?: "view" | "edit";
}

type EventAttachment = {
  id: string;
  name: string;
  type: "media" | "drive" | "url";
  fileUrl?: string;
  fileType?: string;
  webViewLink?: string;
  url?: string;
};

function EventAttachmentsList({ eventId, readOnly = false }: { eventId: string; readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: attachments = [] } = useQuery<EventAttachment[]>({
    queryKey: [`/api/calendar/${eventId}/attachments`],
  });

  const removeAttachmentMutation = useMutation({
    mutationFn: async (att: EventAttachment) => {
      const endpoint = {
        media: `/api/calendar/${eventId}/media/${att.id}`,
        drive: `/api/calendar/${eventId}/drive/${att.id}`,
        url: `/api/calendar/${eventId}/url/${att.id}`,
      }[att.type];
      return apiRequest("DELETE", endpoint);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/calendar/${eventId}/attachments`] });
      toast({
        title: "Attachment Removed",
        description: "The attachment has been removed from this event",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove attachment",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-2" data-testid="event-attachments-list">
      <h4 className="text-sm font-medium flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-muted-foreground" />
        Attachments
      </h4>
      {!readOnly && (
        <DocumentUploadButton
          entityType="calendarEvent"
          entityId={eventId}
          onAttachmentAdded={() =>
            queryClient.invalidateQueries({ queryKey: [`/api/calendar/${eventId}/attachments`] })
          }
        />
      )}
      {attachments.map((att) => {
        const inline = att.type === "media" && (isVideoMediaFile(att) || isImageMediaFile(att));
        return (
          <div key={`${att.type}-${att.id}`} className="rounded-lg border overflow-hidden">
            <div className="flex items-center justify-between gap-2 p-3 bg-muted/30">
              <span className="text-sm font-medium truncate">{att.name}</span>
              {att.type === "media" && att.fileUrl && (
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" asChild>
                  <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" download={att.name}>
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {att.type === "drive" && att.webViewLink && (
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" asChild>
                  <a href={att.webViewLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {att.type === "url" && att.url && (
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" asChild>
                  <a href={att.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    if (removeAttachmentMutation.isPending) return;
                    removeAttachmentMutation.mutate(att);
                  }}
                  disabled={removeAttachmentMutation.isPending}
                  data-testid={`button-remove-attachment-${att.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {inline && <InlineMediaPreview file={att} testId={`attachment-${att.id}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function EventDetailsDialog({ event, onClose, goals = [], defaultMode = "edit" }: EventDetailsDialogProps) {
  const [isEditing, setIsEditing] = useState(defaultMode === "edit");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRecurringExpanded, setIsRecurringExpanded] = useState(false);
  const [recurrenceEndType, setRecurrenceEndType] = useState<"count" | "date">("count");
  // Recurring-series dialog state
  const [recurringAction, setRecurringAction] = useState<"this" | "all" | "change-time">("this");
  const [isSeriesTimeMode, setIsSeriesTimeMode] = useState(false);
  const [newSeriesTime, setNewSeriesTime] = useState("09:00");
  const [newSeriesEndTime, setNewSeriesEndTime] = useState("10:00");
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [cloneDestination, setCloneDestination] = useState("");
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const isExternalEvent = !!event?.externalCalendarId;
  // Checklist-backed events are managed by the checklist scheduler: their
  // recurrence rule is owned by the checklist schedule, and the edit form
  // rebuilds recurrenceRule from generic defaults on save, which would
  // silently rewrite one occurrence's cadence and orphan it from its series.
  // Keep them view-only here (delete + "Open checklist" remain available).
  const isChecklistEvent = !!(event as { checklistId?: string } | null)?.checklistId;
  // Events shared in from another user's calendar carry the authoritative
  // `isSharedEvent: true` flag set by the shared-events endpoint. Attachments
  // are per-owner rows; viewers with an active calendar share can read them
  // (the combined attachments endpoint authorizes via the share), but only
  // the owner can add or remove them.
  const isSharedEvent = !!(event as { isSharedEvent?: boolean } | null)?.isSharedEvent;

  // Fetch additional data for different event types
  const { data: tasks = [] } = useQuery<any[]>({
    queryKey: ["/api/tasks"],
    enabled: !!event,
  });

  const { data: habits = [] } = useQuery<any[]>({
    queryKey: ["/api/habits"],
    enabled: !!event,
  });

  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
    enabled: !!event,
  });

  const { data: internalCalendars = [] } = useQuery<InternalCalendar[]>({
    queryKey: INTERNAL_CALENDARS_QUERY_KEY,
    enabled: !!event,
  });

  const { data: calendarConnections = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar/connections"],
    enabled: !!event,
  });

  const sourceInternalCalendar = internalCalendars.find(
    calendar => calendar.id === (event as CalendarEvent & { internalCalendarId?: string | null } | null)?.internalCalendarId,
  );
  const isPlanningEvent = sourceInternalCalendar?.isPlanningOnly === true;
  const externalCloneCalendars = calendarConnections
    .filter(connection => connection.connected !== false)
    .flatMap(connection =>
      (connection.externalCalendars || []).map((calendar: any) => ({
        ...calendar,
        provider: connection.provider,
      })),
    );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Reset form when event changes
  useEffect(() => {
    if (event) {
      const startDate = new Date(event.startTime);
      const endDate = new Date(event.endTime);

      // Use local date format (YYYY-MM-DD) instead of toISOString which uses UTC
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      form.reset({
        title: event.title || "",
        description: event.description || "",
        eventType: event.eventType || "event",
        category: event.category || undefined,
        allDay: event.allDay || false,
        startDate: formatLocalDate(startDate),
        startTime: format(startDate, 'HH:mm'),
        endDate: formatLocalDate(endDate),
        endTime: format(endDate, 'HH:mm'),
        location: event.location || "",
        color: event.color || "#3b82f6",
        isRecurring: event.isRecurring || false,
        goalId: event.goalId || undefined,
        taskId: event.taskId || undefined,
        habitId: event.habitId || undefined,
        recurrenceRule: event.recurrenceRule || undefined,
        recurrenceFrequency: "weekly",
        recurrenceCount: 4,
        recurrenceEndDate: undefined,
        internalCalendarId: (event as CalendarEvent & { internalCalendarId?: string | null }).internalCalendarId || undefined,
      });
      setIsEditing(defaultMode === "edit" && !(event as { checklistId?: string }).checklistId);
      setIsRecurringExpanded(event.isRecurring || false);
    }
  }, [event, form, defaultMode]);

  useEffect(() => {
    if (event && internalCalendars.length > 0 && !form.getValues("internalCalendarId")) {
      form.setValue(
        "internalCalendarId",
        (internalCalendars.find(calendar => calendar.isDefault) || internalCalendars[0]).id,
      );
    }
  }, [event, internalCalendars, form]);

  const updateEventMutation = useMutation({
    mutationFn: (eventData: any) =>
      apiRequest("PATCH", `/api/calendar/${event?.id}`, eventData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });

      toast({
        title: "Event Updated",
        description: "Your calendar event has been updated successfully",
      });

      // Close dialog and return to calendar immediately after save
      onClose();
    },
    onError: (error: any) => {
      console.error("Event update error:", error);

      const errorMessage = error?.response?.data?.error
        || error?.message
        || "Failed to update event. Please try again.";

      toast({
        title: "Error Updating Event",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const cloneEventMutation = useMutation({
    mutationFn: () => {
      const [destinationType, ...idParts] = cloneDestination.split(":");
      const destinationId = idParts.join(":");
      return apiRequest(
        "POST",
        `/api/calendar/${event?.id}/clone`,
        destinationType === "internal"
          ? { destinationInternalCalendarId: destinationId }
          : { destinationExternalCalendarId: destinationId },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
      setIsCloneDialogOpen(false);
      setCloneDestination("");
      onClose();
      toast({
        title: "Event cloned",
        description: "A separate copy was created in the selected calendar.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't clone event",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Resolve provider-side recurrence while the event is open so delete choices
  // can be shown directly on this screen without another confirmation layer.
  const { data: seriesData, isLoading: isLoadingSeriesData } = useQuery<{ count: number; events: any[] }>({
    queryKey: ["/api/calendar", event?.id, "series"],
    queryFn: () => fetch(`/api/calendar/${event?.id}/series`).then(r => r.json()),
    enabled: !!event?.id && !!event?.externalCalendarId,
    staleTime: 30_000,
  });

  // Treat as recurring if flagged OR if series scan found >= 2 matching events
  const isEffectivelyRecurring = !!event?.isRecurring || (seriesData?.count ?? 0) >= 2;

  const deleteEventMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/calendar/${event?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
      setIsDeleteDialogOpen(false);
      onClose();

      toast({
        title: "Event Deleted",
        description: "The calendar event has been deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error("Event delete error:", error);

      const errorMessage = error?.response?.data?.error
        || error?.message
        || "Failed to delete event. Please try again.";

      toast({
        title: "Error Deleting Event",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const deleteSeriesMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/calendar/${event?.id}?scope=series`),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
      setIsDeleteDialogOpen(false);
      onClose();
      toast({
        title: "Series Deleted",
        description: `Deleted ${data?.deleted ?? "all"} events in this recurring series.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Deleting Series",
        description: error?.message || "Failed to delete series. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateSeriesTimeMutation = useMutation({
    mutationFn: ({ newStartHour, newStartMinute, durationMs }: { newStartHour: number; newStartMinute: number; durationMs: number }) =>
      apiRequest("PATCH", `/api/calendar/${event?.id}/series-time`, { newStartHour, newStartMinute, durationMs }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      setIsDeleteDialogOpen(false);
      setIsSeriesTimeMode(false);
      onClose();
      toast({
        title: "Series Updated",
        description: `Updated time for ${data?.updatedCount ?? "all"} events in this series.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Updating Series",
        description: error?.message || "Failed to update series time. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleRecurringConfirm = () => {
    if (recurringAction === "this") {
      deleteEventMutation.mutate();
    } else if (recurringAction === "all") {
      deleteSeriesMutation.mutate();
    } else if (recurringAction === "change-time") {
      setIsSeriesTimeMode(true);
    }
  };

  const handleSeriesTimeSubmit = () => {
    if (!event) return;
    const [startH, startM] = newSeriesTime.split(":").map(Number);
    const [endH, endM] = newSeriesEndTime.split(":").map(Number);
    const durationMs = ((endH * 60 + endM) - (startH * 60 + startM)) * 60_000;
    if (durationMs <= 0) {
      toast({ title: "Invalid time", description: "End time must be after start time.", variant: "destructive" });
      return;
    }
    updateSeriesTimeMutation.mutate({ newStartHour: startH, newStartMinute: startM, durationMs });
  };

  const onSubmit = (data: FormData) => {
    const { startDate, startTime, endDate, endTime, recurrenceFrequency, recurrenceCount, recurrenceEndDate, ...rest } = data;

    let startDateTime: Date;
    let endDateTime: Date;

    // Parse date string "YYYY-MM-DD" into local date components to avoid timezone issues
    const parseLocalDate = (dateStr: string, hours: number, minutes: number, seconds: number, ms: number) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, hours, minutes, seconds, ms);
    };

    if (rest.allDay) {
      // For all-day events, create dates at local midnight/end of day
      startDateTime = parseLocalDate(startDate, 0, 0, 0, 0);
      endDateTime = parseLocalDate(endDate, 23, 59, 59, 999);
    } else {
      // For timed events, parse the time components
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      startDateTime = parseLocalDate(startDate, startHour, startMin, 0, 0);
      endDateTime = parseLocalDate(endDate, endHour, endMin, 0, 0);
    }

    let recurrenceRule = undefined;
    if (rest.isRecurring && recurrenceFrequency) {
      const freq = recurrenceFrequency.toUpperCase().replace('-', '');
      if (recurrenceEndType === "count" && recurrenceCount) {
        recurrenceRule = `FREQ=${freq};COUNT=${recurrenceCount}`;
      } else if (recurrenceEndType === "date" && recurrenceEndDate) {
        // Use parseLocalDate to avoid UTC shift for recurrence end date
        const untilDate = parseLocalDate(recurrenceEndDate, 23, 59, 59, 0);
        const untilStr = untilDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        recurrenceRule = `FREQ=${freq};UNTIL=${untilStr}`;
      }
    }

    const cleanedData = {
      ...rest,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      recurrenceRule: recurrenceRule,
      goalId: rest.goalId === "none" || !rest.goalId ? undefined : rest.goalId,
      taskId: rest.taskId === "none" || !rest.taskId ? undefined : rest.taskId,
      habitId: rest.habitId === "none" || !rest.habitId ? undefined : rest.habitId,
      category: rest.category === "none" || !rest.category ? undefined : rest.category,
    };

    console.log("Updating event with data:", cleanedData);

    updateEventMutation.mutate(cleanedData);
  };

  const handleNavigateToLinkedItem = () => {
    if (event?.goalId) {
      setLocation(`/goals`);
      onClose();
    } else if (event?.taskId) {
      setLocation(`/tasks`);
      onClose();
    } else if ((event as any)?.checklistId) {
      setLocation(`/checklists?open=${(event as any).checklistId}`);
      onClose();
    }
  };

  const getEventTypeIcon = (eventType?: string) => {
    switch (eventType) {
      case "goal":
        return <Target className="h-4 w-4" />;
      case "task":
        return <CheckSquare className="h-4 w-4" />;
      case "reminder":
        return <Clock className="h-4 w-4" />;
      default:
        return <CalendarIcon className="h-4 w-4" />;
    }
  };

  const getLinkedItemName = () => {
    if (event?.goalId) {
      const goal = goals.find(g => g.id === event.goalId);
      return goal?.title || "Linked Goal";
    } else if (event?.taskId) {
      const task = tasks.find(t => t.id === event.taskId);
      return task?.title || "Linked Task";
    } else if (event?.habitId) {
      const habit = habits.find(h => h.id === event.habitId);
      return habit?.name || "Linked Habit";
    } else if ((event as any)?.checklistId) {
      return "Open checklist";
    }
    return null;
  };

  // Human-readable cadence from the event's recurrence rule, e.g. "daily" /
  // "weekly", so delete confirmations can say exactly what repeats.
  const recurrenceFrequencyLabel = (() => {
    const rule = event?.recurrenceRule || "";
    const m = /FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)/i.exec(rule);
    if (!m) return null;
    switch (m[1].toUpperCase()) {
      case "DAILY": return "daily";
      case "WEEKLY": return "weekly";
      case "MONTHLY": return "monthly";
      default: return "yearly";
    }
  })();

  if (!event) return null;

  return (
    <>
      <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto"
          data-testid="dialog-event-details"
          onInteractOutside={preventDismissFromSiblingDialog}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                {getEventTypeIcon(event.eventType)}
                {isEditing ? "Edit Event" : "Event Details"}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {isExternalEvent && (
                  <Badge variant="outline" className="gap-1">
                    <ExternalLink className="h-3 w-3" />
                    External
                  </Badge>
                )}
                {isPlanningEvent && !isSharedEvent && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsCloneDialogOpen(true)}
                    data-testid="button-clone-event"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Clone
                  </Button>
                )}
                {!isExternalEvent && !isChecklistEvent && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsShareDialogOpen(true)}
                      data-testid="button-share-event"
                    >
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                    {!isEditing && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                        data-testid="button-edit-event"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </DialogHeader>

          {isEditing && !isExternalEvent ? (
            <div className="max-w-4xl mx-auto p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Event title..." data-testid="input-event-title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Event Type */}
                  <FormField
                    control={form.control}
                    name="eventType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-event-type">
                              <SelectValue placeholder="Select event type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="event">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4" />
                                Event
                              </div>
                            </SelectItem>
                            <SelectItem value="task">
                              <div className="flex items-center gap-2">
                                <CheckSquare className="h-4 w-4" />
                                Task
                              </div>
                            </SelectItem>
                            <SelectItem value="goal">
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4" />
                                Goal Event
                              </div>
                            </SelectItem>
                            <SelectItem value="milestone">
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4" />
                                Milestone
                              </div>
                            </SelectItem>
                            <SelectItem value="habit">
                              <div className="flex items-center gap-2">
                                <Repeat className="h-4 w-4" />
                                Habit Reminder
                              </div>
                            </SelectItem>
                            <SelectItem value="reminder">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Reminder
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Category Selection */}
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger data-testid="select-event-category">
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No Category</SelectItem>
                            <CategorySelectGroups categories={categories} />
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Goal Selection (if goal event type) */}
                  {form.watch("eventType") === "goal" && (
                    <FormField
                      control={form.control}
                      name="goalId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Related Goal</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a goal" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">No Goal</SelectItem>
                              {goals.filter(goal => !isFutureGoal(goal)).map(goal => (
                                <SelectItem key={goal.id} value={goal.id}>
                                  {goal.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Task Selection (if task event type) */}
                  {form.watch("eventType") === "task" && (
                    <FormField
                      control={form.control}
                      name="taskId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Related Task (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a task" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">No Task</SelectItem>
                              {tasks.map((task: any) => (
                                <SelectItem key={task.id} value={task.id}>
                                  {task.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Habit Selection (if habit reminder type) */}
                  {form.watch("eventType") === "habit" && (
                    <FormField
                      control={form.control}
                      name="habitId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Related Habit</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a habit" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">No Habit</SelectItem>
                              {habits.map((habit: any) => (
                                <SelectItem key={habit.id} value={habit.id}>
                                  {habit.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ""}
                            placeholder="Event description..."
                            className="resize-none"
                            rows={3}
                            data-testid="input-event-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* All Day Toggle */}
                  <FormField
                    control={form.control}
                    name="allDay"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="allDay"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-all-day"
                        />
                        <label htmlFor="allDay" className="text-sm font-medium">
                          All day event
                        </label>
                      </div>
                    )}
                  />

                  {/* Date and Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="input-event-start-date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="input-event-end-date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {!form.watch("allDay") && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} data-testid="input-event-start-time" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="endTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>End Time</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} data-testid="input-event-end-time" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Location */}
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              value={field.value || ""}
                              placeholder="Event location..."
                              className="pl-9"
                              data-testid="input-event-location"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Color */}
                  {internalCalendars.length > 0 && (
                    <FormField
                      control={form.control}
                      name="internalCalendarId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Calendar</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-edit-internal-calendar">
                                <SelectValue placeholder="Select a calendar" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {internalCalendars.map(calendar => (
                                <SelectItem key={calendar.id} value={calendar.id}>
                                  <span className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: calendar.color || "#3b82f6" }} />
                                    {calendar.name}{calendar.isPlanningOnly ? " (Planning only)" : ""}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Color */}
                  <FormField
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Color</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || "#3b82f6"}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="#3b82f6">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#3b82f6" }} />
                                Blue
                              </div>
                            </SelectItem>
                            <SelectItem value="#ef4444">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#ef4444" }} />
                                Red
                              </div>
                            </SelectItem>
                            <SelectItem value="#22c55e">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#22c55e" }} />
                                Green
                              </div>
                            </SelectItem>
                            <SelectItem value="#f59e0b">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#f59e0b" }} />
                                Yellow
                              </div>
                            </SelectItem>
                            <SelectItem value="#8b5cf6">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#8b5cf6" }} />
                                Purple
                              </div>
                            </SelectItem>
                            <SelectItem value="#06b6d4">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#06b6d4" }} />
                                Cyan
                              </div>
                            </SelectItem>
                            <SelectItem value="#ec4899">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#ec4899" }} />
                                Pink
                              </div>
                            </SelectItem>
                            <SelectItem value="#6b7280">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#6b7280" }} />
                                Gray
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Recurring Section - Only for Tasks */}
                  {form.watch("eventType") === "task" && (
                    <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Repeat className="h-4 w-4" />
                          <span className="font-medium">Recurring Task</span>
                        </div>
                        <Button
                          type="button"
                          variant={form.watch("isRecurring") ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const current = form.getValues("isRecurring");
                            form.setValue("isRecurring", !current);
                            setIsRecurringExpanded(!current);
                          }}
                          data-testid="button-toggle-recurring"
                        >
                          {form.watch("isRecurring") ? (
                            <>
                              <ChevronUp className="h-4 w-4 mr-1" />
                              Enabled
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4 mr-1" />
                              Enable Recurring
                            </>
                          )}
                        </Button>
                      </div>

                      {form.watch("isRecurring") && isRecurringExpanded && (
                        <div className="space-y-3 pt-2">
                          {/* Frequency Selector */}
                          <FormField
                            control={form.control}
                            name="recurrenceFrequency"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Frequency</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value || "weekly"}>
                                  <FormControl>
                                    <SelectTrigger data-testid="select-recurrence-frequency">
                                      <SelectValue placeholder="Select frequency" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="daily">Daily</SelectItem>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                    <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                    <SelectItem value="quarterly">Quarterly</SelectItem>
                                    <SelectItem value="yearly">Yearly</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* End Condition */}
                          <div className="space-y-2">
                            <FormLabel>End Condition</FormLabel>
                            <RadioGroup
                              value={recurrenceEndType}
                              onValueChange={(value: "count" | "date") => setRecurrenceEndType(value)}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="count" id="end-count" data-testid="radio-end-count" />
                                <label htmlFor="end-count" className="text-sm flex-1">
                                  Number of occurrences
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="date" id="end-date" data-testid="radio-end-date" />
                                <label htmlFor="end-date" className="text-sm flex-1">
                                  End date
                                </label>
                              </div>
                            </RadioGroup>
                          </div>

                          {/* Occurrence Count Input */}
                          {recurrenceEndType === "count" && (
                            <FormField
                              control={form.control}
                              name="recurrenceCount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Number of Occurrences</FormLabel>
                                  <FormControl>
                                    <NumberField
                                      min={1}
                                      max={100}
                                      emptyValue={1}
                                      value={field.value}
                                      onValueChange={(v) => field.onChange(v ?? 1)}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                      ref={field.ref}
                                      data-testid="input-recurrence-count"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          {/* End Date Input */}
                          {recurrenceEndType === "date" && (
                            <FormField
                              control={form.control}
                              name="recurrenceEndDate"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>End Date</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="date"
                                      {...field}
                                      value={field.value || ""}
                                      data-testid="input-recurrence-end-date"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          {/* Recurrence Summary */}
                          {form.watch("recurrenceFrequency") && (
                            <div className="text-sm text-muted-foreground bg-background p-3 rounded-md border">
                              <p className="font-medium mb-1">Recurrence Summary:</p>
                              <p>
                                Creating <strong>{form.watch("recurrenceFrequency")}</strong> tasks at{" "}
                                <strong>{form.watch("startTime") || "09:00"}</strong>
                                {recurrenceEndType === "count" && form.watch("recurrenceCount") && (
                                  <> for <strong>{form.watch("recurrenceCount")}</strong> occurrences</>
                                )}
                                {recurrenceEndType === "date" && form.watch("recurrenceEndDate") && (
                                  <> until <strong>{new Date(form.watch("recurrenceEndDate")!).toLocaleDateString()}</strong></>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
                    <div className="flex gap-2 sm:mr-auto">
                      {isEffectivelyRecurring ? (
                        <>
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={() => deleteEventMutation.mutate()}
                            disabled={deleteEventMutation.isPending || deleteSeriesMutation.isPending}
                            data-testid="button-delete-event"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete this event
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={() => deleteSeriesMutation.mutate()}
                            disabled={deleteEventMutation.isPending || deleteSeriesMutation.isPending}
                            data-testid="button-delete-event-series"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete entire series
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => deleteEventMutation.mutate()}
                          disabled={deleteEventMutation.isPending || isLoadingSeriesData}
                          data-testid="button-delete-event"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {isLoadingSeriesData ? "Checking event..." : "Delete"}
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        data-testid="button-cancel-edit"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={updateEventMutation.isPending}
                        data-testid="button-save-event"
                      >
                        {updateEventMutation.isPending ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </DialogFooter>
                </form>
              </Form>
              <div className="mt-6">
                <EventAttachmentsList eventId={event.id} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="text-2xl font-semibold" data-testid="text-event-title">{event.title}</h3>
                {event.description && (
                  <p className="text-muted-foreground mt-2" data-testid="text-event-description">{event.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2" data-testid="text-event-time">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {format(new Date(event.startTime), 'PPp')} - {format(new Date(event.endTime), 'PPp')}
                  </span>
                </div>

                {event.location && (
                  <div className="flex items-center gap-2" data-testid="text-event-location">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{event.location}</span>
                  </div>
                )}

                {event.isRecurring && (
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-muted-foreground" />
                    <span>Recurring event</span>
                  </div>
                )}

                {getLinkedItemName() && (
                  <div className="flex items-center gap-2">
                    {getEventTypeIcon(event.eventType)}
                    <Button
                      variant="link"
                      className="p-0 h-auto"
                      onClick={handleNavigateToLinkedItem}
                      data-testid="button-view-linked-item"
                    >
                      {getLinkedItemName()}
                    </Button>
                  </div>
                )}

                {event.isCompleted && (
                  <div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Completed
                    </Badge>
                  </div>
                )}
              </div>

              <EventAttachmentsList eventId={event.id} readOnly={isSharedEvent} />

              <DialogFooter className="gap-2">
                {isEffectivelyRecurring ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => deleteEventMutation.mutate()}
                      disabled={deleteEventMutation.isPending || deleteSeriesMutation.isPending}
                      data-testid="button-delete-event"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete this event
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => deleteSeriesMutation.mutate()}
                      disabled={deleteEventMutation.isPending || deleteSeriesMutation.isPending}
                      data-testid="button-delete-event-series"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete entire series
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={() => deleteEventMutation.mutate()}
                    disabled={deleteEventMutation.isPending || isLoadingSeriesData}
                    data-testid="button-delete-event"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {isLoadingSeriesData ? "Checking event..." : "Delete"}
                  </Button>
                )}
                <Button onClick={onClose} data-testid="button-close-dialog">
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCloneDialogOpen}
        onOpenChange={(open) => {
          setIsCloneDialogOpen(open);
          if (!open) setCloneDestination("");
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="dialog-clone-event"
          onInteractOutside={preventDismissFromSiblingDialog}
        >
          <DialogHeader>
            <DialogTitle>Clone event</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="clone-destination">Destination calendar</label>
            <Select value={cloneDestination} onValueChange={setCloneDestination}>
              <SelectTrigger id="clone-destination" data-testid="select-clone-destination">
                <SelectValue placeholder="Choose a calendar" />
              </SelectTrigger>
              <SelectContent>
                {internalCalendars.map(calendar => (
                  <SelectItem key={`internal-${calendar.id}`} value={`internal:${calendar.id}`}>
                    GoalVia · {calendar.name}{calendar.isPlanningOnly ? " (Planning only)" : ""}
                  </SelectItem>
                ))}
                {externalCloneCalendars.map((calendar: any) => (
                  <SelectItem key={`external-${calendar.id}`} value={`external:${calendar.id}`}>
                    {calendar.provider || "Connected"} · {calendar.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Cloning creates a separate event. The planning event remains unchanged.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsCloneDialogOpen(false)}
              data-testid="button-cancel-clone"
            >
              Cancel
            </Button>
            <Button
              onClick={() => cloneEventMutation.mutate()}
              disabled={!cloneDestination || cloneEventMutation.isPending}
              data-testid="button-confirm-clone"
            >
              {cloneEventMutation.isPending ? "Cloning..." : "Clone event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — recurring-aware */}
      <AlertDialog open={isDeleteDialogOpen && !isEffectivelyRecurring} onOpenChange={(open) => { if (!open) { setIsDeleteDialogOpen(false); } }}>
        <AlertDialogContent data-testid="dialog-delete-confirmation" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              {isExternalEvent
                ? "Are you sure you want to delete this event? This will also remove it from Google Calendar. This action cannot be undone."
                : "Are you sure you want to delete this event? This action cannot be undone."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEventMutation.mutate()}
              disabled={deleteEventMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteEventMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recurring Event Options Dialog */}
      <Dialog
        open={isDeleteDialogOpen && isEffectivelyRecurring}
        onOpenChange={(open) => {
          if (!open) {
            setIsDeleteDialogOpen(false);
            setIsSeriesTimeMode(false);
            setRecurringAction("this");
          }
        }}
      >
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-recurring-options">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-blue-500" />
              {isSeriesTimeMode ? "Change Time for All Events" : "Recurring Event"}
            </DialogTitle>
          </DialogHeader>

          {!isSeriesTimeMode ? (
            <div className="space-y-6 py-2">
              <p className="text-sm text-muted-foreground">
                <strong>&ldquo;{event?.title}&rdquo;</strong> is a recurring event
                {recurrenceFrequencyLabel ? ` that repeats ${recurrenceFrequencyLabel}` : ""}
                {seriesData?.count && seriesData.count > 1 ? ` (${seriesData.count} occurrences found)` : ""}.
                What would you like to do?
              </p>

              <div className="space-y-3">
                {/* Option 1 */}
                <button
                  type="button"
                  onClick={() => setRecurringAction("this")}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${
                    recurringAction === "this"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="option-delete-this"
                >
                  <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${recurringAction === "this" ? "border-blue-500" : "border-muted-foreground"}`}>
                    {recurringAction === "this" && <div className="h-2 w-2 rounded-full bg-blue-500" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">Delete only this event</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Remove just this single occurrence. Other events in the series will remain.</p>
                  </div>
                </button>

                {/* Option 2 */}
                <button
                  type="button"
                  onClick={() => setRecurringAction("all")}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${
                    recurringAction === "all"
                      ? "border-red-500 bg-red-50 dark:bg-red-950/30"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="option-delete-all"
                >
                  <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${recurringAction === "all" ? "border-red-500" : "border-muted-foreground"}`}>
                    {recurringAction === "all" && <div className="h-2 w-2 rounded-full bg-red-500" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">Delete all events</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Permanently remove every occurrence in this {recurrenceFrequencyLabel ? `${recurrenceFrequencyLabel} ` : ""}series
                      {seriesData?.count && seriesData.count > 1 ? ` (${seriesData.count} events)` : ""}.
                      {isExternalEvent && " Also removes them from Google Calendar."}
                    </p>
                  </div>
                </button>

                {/* Option 3 */}
                <button
                  type="button"
                  onClick={() => setRecurringAction("change-time")}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${
                    recurringAction === "change-time"
                      ? "border-green-500 bg-green-50 dark:bg-green-950/30"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="option-change-time"
                >
                  <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${recurringAction === "change-time" ? "border-green-500" : "border-muted-foreground"}`}>
                    {recurringAction === "change-time" && <div className="h-2 w-2 rounded-full bg-green-500" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5" />
                      Change date and time of all events
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Update the time for every occurrence in this series to a new time.</p>
                  </div>
                </button>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setRecurringAction("this");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={recurringAction === "all" ? "destructive" : recurringAction === "change-time" ? "default" : "destructive"}
                  onClick={handleRecurringConfirm}
                  disabled={deleteEventMutation.isPending || deleteSeriesMutation.isPending}
                  data-testid="button-confirm-recurring-action"
                >
                  {deleteEventMutation.isPending || deleteSeriesMutation.isPending
                    ? "Processing..."
                    : recurringAction === "change-time"
                    ? "Next: Set New Time →"
                    : "Confirm"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Change time sub-form */
            <div className="space-y-6 py-2">
              <p className="text-sm text-muted-foreground">
                Choose a new time for <strong>all occurrences</strong> of &ldquo;{event?.title}&rdquo;. Each event will keep its original date but will be rescheduled to this new time.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">New Start Time</label>
                  <Input
                    type="time"
                    value={newSeriesTime}
                    onChange={(e) => setNewSeriesTime(e.target.value)}
                    data-testid="input-series-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">New End Time</label>
                  <Input
                    type="time"
                    value={newSeriesEndTime}
                    onChange={(e) => setNewSeriesEndTime(e.target.value)}
                    data-testid="input-series-end-time"
                  />
                </div>
              </div>
              {seriesData?.count && seriesData.count > 1 && (
                <p className="text-xs text-muted-foreground bg-muted/50 rounded p-3">
                  This will update <strong>{seriesData.count} events</strong> in the series.
                  {isExternalEvent && " Google Calendar will also be updated."}
                </p>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setIsSeriesTimeMode(false)}>
                  ← Back
                </Button>
                <Button
                  onClick={handleSeriesTimeSubmit}
                  disabled={updateSeriesTimeMutation.isPending}
                  data-testid="button-confirm-series-time"
                >
                  {updateSeriesTimeMutation.isPending ? "Updating..." : "Update All Events"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {event && (
        <ShareDialog
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          itemId={event.id}
          itemType="calendar_event"
          itemTitle={event.title}
        />
      )}
    </>
  );
}
